import type { ReactNode } from "react";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { motion } from "framer-motion";

import { paper, fonts, springs, shade, calmLoop } from "../theme";
import { MsCircleBtn } from "../ms";

/**
 * Unified notification primitive.
 *
 * One light "paper card" look for every transient message in the app —
 * connection status, PWA updates, home-screen errors, resume prompts. Severity
 * is conveyed by a tone-coloured accent line along the bottom edge rather than
 * by recolouring the whole card, so an error and a success read as the same
 * object with a different underline.
 *
 * Compose it as: <NoticeStack><AnimatePresence>{maybeNotice}</AnimatePresence></NoticeStack>
 * where each Notice carries a stable `key`.
 */

export type NoticeTone = "info" | "success" | "error";

const toneColor: Record<NoticeTone, string> = {
    info: paper.ink,
    success: paper.sage,
    error: paper.red,
};

/** Fixed, top-centre rail that stacks one or more notices. */
export const NoticeStack = styled.div`
    position: fixed;
    top: calc(var(--safe-top) + 8px);
    left: 50%;
    transform: translateX(-50%);
    z-index: 2000;
    pointer-events: none;
    width: max-content;
    max-width: calc(100vw - 24px);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
`;

const Card = styled(motion.div)<{ tone: NoticeTone }>`
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 11px 14px 13px;
    background: ${paper.paperCool};
    color: ${paper.ink};
    border-radius: 10px;
    box-shadow:
        0 0 0 1px ${shade(0.08)},
        0 12px 24px -8px ${shade(0.35)};
    font-family: ${fonts.sans};
    font-size: 14px;
    max-width: min(460px, calc(100vw - 24px));
    pointer-events: auto;

    &::after {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 3px;
        background: ${p => toneColor[p.tone]};
    }
`;

const Message = styled.div`
    flex: 1;
    min-width: 0;
    font-weight: 600;
`;

const Actions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
`;

const NoticeBtn = styled.button<{ primary?: boolean }>`
    background: ${p => (p.primary ? paper.red : "transparent")};
    color: ${p => (p.primary ? paper.paper : paper.inkSoft)};
    border: 0;
    border-radius: 6px;
    padding: 6px 12px;
    font-family: ${fonts.sans};
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
    transition: filter 120ms ease, background 120ms ease;

    &:hover {
        filter: brightness(0.96);
        background: ${p => (p.primary ? paper.red : shade(0.06))};
    }
`;

/* The ghost circle press, sized down and carrying the × glyph. */
const DismissBtn = styled(MsCircleBtn)`
    font-size: 14px;
    line-height: 1;
`;

const spin = keyframes`to { transform: rotate(360deg); }`;

/** Small spinner for in-flight notices (e.g. reconnecting). */
export const NoticeSpinner = styled.span<{ tone?: NoticeTone }>`
    width: 14px;
    height: 14px;
    border-radius: 50%;
    border: 2px solid ${shade(0.18)};
    border-top-color: ${p => toneColor[p.tone ?? "info"]};
    flex-shrink: 0;
    animation: ${spin} 0.9s linear infinite;
    ${calmLoop}
`;

/** Small pulsing dot for steady-state notices (e.g. offline / reconnected). */
export const NoticeDot = styled.span<{ tone?: NoticeTone }>`
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: ${p => toneColor[p.tone ?? "info"]};
    box-shadow: 0 0 0 4px ${p => `${toneColor[p.tone ?? "info"]}22`};
    flex-shrink: 0;
    animation: ms-tick-pulse 1.4s ease-in-out infinite;
    ${calmLoop}
`;

const anim = {
    initial: { opacity: 0, y: -14, scale: 0.96 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, y: -10, scale: 0.96 },
    transition: springs.pill,
};

export interface NoticeAction {
    label: string;
    onClick: () => void;
    primary?: boolean;
}

interface NoticeProps {
    tone?: NoticeTone;
    /** Leading visual — a <NoticeSpinner/> or <NoticeDot/>, or any node. */
    icon?: ReactNode;
    children: ReactNode;
    actions?: NoticeAction[];
    onDismiss?: () => void;
    /** role defaults to alert for errors, status otherwise. */
    role?: "status" | "alert";
}

export function Notice({ tone = "info", icon, children, actions, onDismiss, role }: NoticeProps) {
    return (
        <Card
            tone={tone}
            role={role ?? (tone === "error" ? "alert" : "status")}
            aria-live={tone === "error" ? "assertive" : "polite"}
            {...anim}
        >
            {icon}
            <Message>{children}</Message>
            {(actions?.length || onDismiss) && (
                <Actions>
                    {actions?.map(a => (
                        <NoticeBtn key={a.label} primary={a.primary} onClick={a.onClick}>{a.label}</NoticeBtn>
                    ))}
                    {onDismiss && (
                        <DismissBtn
                            type="button"
                            tone="ghost"
                            size={24}
                            onClick={onDismiss}
                            aria-label="Dismiss"
                        >×</DismissBtn>
                    )}
                </Actions>
            )}
        </Card>
    );
}
